import { useReducer, MouseEvent, useEffect } from 'react'
import { Avatar, Menu, Badge } from '@mui/material'
import {
    PeopleOutline as PeopleOutlineIcon,
    Logout as LogoutIcon,
    NotificationsActive as NotificationsActiveIcon,
    Menu as MenuIcon,
    DynamicFeed as DynamicFeedIcon,
    PhoneIphone as PhoneIphoneIcon,
    Insights,
    HelpOutlineOutlined as HelpOutlineOutlinedIcon,
    MenuBookOutlined as MenuBookOutlinedIcon,
    Checklist as ChecklistIcon,
    Close as CloseIcon,
    NotificationsOff as NotificationsOffIcon,
    Balance as BalanceIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material'
import UserForm from '../Admin/Users/UserAdmin/UserForm'
import FAQ from '../FAQ'
import UserGuidePdf from '../UserGuide'
import { getUser, editUser } from '../../apiRoutes'
import { useToastMessage } from '../../context/ToastPopUpContext'

import {
    IconButton,
    Box,
    Tooltip,
    Grid,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListItem,
    Typography,
    Chip,
    Divider,
} from '@mui/material'
import { useAuth, useEmulation } from '../../context/AuthenticationContext'
import { ImpersonateDialog, ImpersonationBanner } from './Emulation'
import { useSocket } from '../../context/SocketContext'
import { UniversalModal } from '../UniversalComponents'
import LegalUploader from '../LegalDocumentationUploader/LegalUploader'
import NavMenuNotificationsModal from './NavMenuNotificationsModal'
import { isExcludedNotification } from '../UserNotifications'
import { useVersionChecker } from '../../context/VersionCheckerContext'
import { permissionsCheck } from '../../utilities/functionTools'
import { usePermissions } from '../../context/AuthenticationContext'
import { useGlobalState } from '../../context/GlobalStateContext'

export const getData = async (_id: string, setState: any) => {
    try {
        const user = await getUser({ _id })

        if (user) {
            setState({ user, userNotifications: user.userNotifications })
        } else {
            throw new Error('No user found')
        }
    } catch (error) {
        console.error('error', error)
    }
}

const NavMenuAvatar = ({ sx }: any) => {
    const authentication = useAuth()
    const { apiSocket } = useSocket()
    const openChangeLog = useVersionChecker()
    const { signOutApp } = authentication
    const { realAuth, isEmulating } = useEmulation()
    const realSuperAdmin = realAuth?.userProfile?.superAdmin === true
    const roleLabel = realSuperAdmin
        ? 'Super Admin'
        : realAuth?.userProfile?.dealerProfile
        ? 'Dealer'
        : 'Manufacturer'
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { userEmail, _id } = oupUser

    const defaultState = {
        anchorEl: null,
        anchorUser: null,
        username: userEmail?.toUpperCase(),
        openUser: false,
        openBoats: false,
        openEmulate: false,
        openLegal: false,
        userNotifications: [],
        notificationsEnabled: true,
        openNotifications: false,

        user: oupUser,
    }
    const reducer = (oldState, newState) => ({ ...oldState, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        notifications,
        openFaq,
        openUserGuide,
        openLegal,
        notificationsEnabled,
        user,
        userNotifications,
        username,
    } = state

    const setToastMessage = useToastMessage()

    const handleClearNotifications = async () => {
        try {
            const newUserNotificationsArchived = userNotifications?.length
                ? [...userNotifications]
                : []

            await editUser({
                ...user,
                id: user._id,
                userNotifications: [],
                userNotificationsArchived: newUserNotificationsArchived,
            })
            setState({ userNotifications: [] })
        } catch (err) {
            console.error(err)
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleDeleteNotification = async (deleteId: any) => {
        const newUserNotificationsArchived = userNotifications
            ?.filter((notification) => notification?._id === deleteId)
            ?.map(({ _id }: any) => ({
                _id,
            }))

        const findNotifications = userNotifications?.filter(
            (notification) => notification?._id !== deleteId
        )

        const notificationIdsMongo = findNotifications?.map(({ _id }: any) => ({
            _id,
        }))

        try {
            await editUser({
                ...user,
                id: user._id,
                userNotifications: notificationIdsMongo,
                userNotificationsArchived: newUserNotificationsArchived,
            })
            setState({ userNotifications: findNotifications })
        } catch (err) {
            console.error(err)
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
        setState({
            anchorUser: event.currentTarget,
            anchorEl: event.currentTarget,
        })
    }

    const disableNotifications = () => {
        setState({ notificationsEnabled: !state.notificationsEnabled })
    }

    const handleCloseUserMenu = () => {
        setState({ anchorUser: defaultState.anchorUser })
    }

    const handleOpenNotifications = () => {
        setState({ openNotifications: !state.openNotifications })
    }

    const logout = () => {
        signOutApp()
    }

    const loggedIn = [
        {
            title: 'Account',
            onClick: () =>
                setState({ openUser: !state.openUser, anchorUser: null }),
            icon: <PeopleOutlineIcon />,
        },
        // God mode — only for real super admins, and only when not already
        // emulating (exit is via the floating pill).
        // ...(realSuperAdmin && !isEmulating
        //     ? [
        //           {
        //               title: 'Impersonate',
        //               onClick: () =>
        //                   setState({ openEmulate: true, anchorUser: null }),
        //               icon: <VisibilityIcon />,
        //           },
        //       ]
        //     : []),
        {
            title: 'Change Log',
            onClick: openChangeLog,
            icon: <ChecklistIcon />,
        },
        {
            title: 'FAQ',
            onClick: () => setState({ openFaq: !openFaq, anchorUser: null }),
            icon: <HelpOutlineOutlinedIcon />,
        },
        {
            title: 'User Guide',
            onClick: () =>
                setState({ openUserGuide: !openUserGuide, anchorUser: null }),
            icon: <MenuBookOutlinedIcon />,
        },
        {
            title: 'Legal Agreements',
            onClick: () =>
                setState({ openLegal: !openLegal, anchorUser: null }),
            icon: <BalanceIcon />,
        },
        { title: 'Logout', onClick: logout, icon: <LogoutIcon /> },
    ]

    //   const getData = async () => {
    //         try {
    //             const user = await getUser({ _id })

    //             if (user) {
    //                 setState({ user, userNotifications: user.userNotifications })
    //             } else {
    //                 throw new Error('No user found')
    //             }
    //         } catch (error) {
    //             console.error('error')
    //         }
    //     }

    useEffect(() => {
        apiSocket?.on(_id, () => {
            getData(_id, setState)
        })
        getData(_id, setState)
    }, [])

    const setModalVisbility = async (user: any, newData: any) => {
        if (user) {
            setState({ row: user })
        } else {
            setState({
                row: defaultState.user,
            })
        }
        if (newData !== undefined) {
            return getData(_id, setState)
        }
    }

    const handleMarkAsRead = async (id: string) => {
        const newUserNotifications = userNotifications.map((n) =>
            n._id === id ? { ...n, read: !n.read } : n
        )

        try {
            await editUser({
                ...user,
                id: user._id,
                userNotifications: newUserNotifications,
            })

            setState({ userNotifications: newUserNotifications })
        } catch (err) {
            console.error(err)
            setToastMessage(`${err}`, 'error')
        }
    }
    useEffect(() => {
        const handleRefresh = () => {
            if (_id) getData(_id, setState)
        }

        window.addEventListener('notifications:refresh', handleRefresh)

        return () =>
            window.removeEventListener('notifications:refresh', handleRefresh)
    }, [_id])

    // useEffect(() => {
    //     if (userEmail && userEmail !== '') {
    //         setState({ username: userEmail?.toUpperCase() })
    //     }
    // }, [userEmail])

    return (
        <Grid
            container
            spacing={2}
            id="menu-grid"
            sx={{ marginRight: 5, position: 'relative', top: 9 }}
        >
            <Grid container>
                <Grid size={6}>
                    <Tooltip title="Open settings">
                        <IconButton onClick={handleOpenUserMenu}>
                            <Avatar
                                src=""
                                sx={{
                                    color: 'inherit',
                                }}
                                variant="rounded"
                            >
                                {username &&
                                    username?.length !== 0 &&
                                    username?.substring(0, 1)}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid size={6}>
                    <Tooltip title="Open Notifications Panel">
                        <Typography
                            color={'primary'}
                            align="center"
                            sx={{ margin: 2, position: 'relative', bottom: 3 }}
                        >
                            <Badge
                                badgeContent={
                                    state.notificationsEnabled
                                        ? (userNotifications || []).filter(
                                              (n: any) =>
                                                  !isExcludedNotification(n) &&
                                                  !n.read
                                          ).length
                                        : null
                                }
                                color="primary"
                            >
                                <IconButton
                                    onClick={handleOpenNotifications}
                                    sx={{ color: 'primary' }}
                                >
                                    {state.notificationsEnabled ? (
                                        <NotificationsActiveIcon />
                                    ) : (
                                        <NotificationsOffIcon />
                                    )}
                                </IconButton>
                            </Badge>
                        </Typography>
                    </Tooltip>
                </Grid>

                <Menu
                    id="menu-appbar"
                    anchorEl={state.anchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    keepMounted
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={Boolean(state.anchorUser)}
                    onClose={handleCloseUserMenu}
                    PaperProps={{
                        elevation: 8,
                        sx: {
                            mt: 1,
                            minWidth: 268,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                        },
                    }}
                >
                    {/* Account header */}
                    <Box
                        sx={{
                            px: 2,
                            pt: 1.75,
                            pb: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                        }}
                    >
                        <Avatar
                            variant="rounded"
                            sx={{
                                bgcolor: 'primary.main',
                                color: '#221F1F',
                                fontWeight: 700,
                                width: 38,
                                height: 38,
                            }}
                        >
                            {username && username.substring(0, 1)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                title={userEmail}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 13,
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 180,
                                }}
                            >
                                {userEmail || username}
                            </Typography>
                            <Chip
                                label={roleLabel}
                                size="small"
                                sx={{
                                    mt: 0.5,
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: 'rgba(139,196,65,0.16)',
                                    color: 'primary.main',
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ py: 0.5 }}>
                        {loggedIn.map(({ title, onClick, icon }) => {
                            const isLogout = title === 'Logout'
                            const isImpersonate = title === 'Impersonate'
                            return (
                                <Box key={`${title}.button`}>
                                    {isLogout && <Divider sx={{ my: 0.5 }} />}
                                    <ListItemButton
                                        onClick={onClick}
                                        sx={{
                                            mx: 1,
                                            borderRadius: 1.5,
                                            py: 0.6,
                                            color: isLogout
                                                ? 'error.main'
                                                : isImpersonate
                                                ? '#A78BFA'
                                                : 'text.primary',
                                            '&:hover': {
                                                bgcolor: isImpersonate
                                                    ? 'rgba(139,92,246,0.14)'
                                                    : 'action.hover',
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 34,
                                                color: 'inherit',
                                            }}
                                        >
                                            {icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={title}
                                            primaryTypographyProps={{
                                                fontSize: 13,
                                                fontWeight: isImpersonate
                                                    ? 700
                                                    : 500,
                                            }}
                                        />
                                    </ListItemButton>
                                </Box>
                            )
                        })}
                    </Box>
                </Menu>
                <NavMenuNotificationsModal
                    open={state.openNotifications}
                    onClose={handleOpenNotifications}
                    userNotifications={state.userNotifications}
                    onClearNotifications={handleClearNotifications}
                    handleDeleteNotification={handleDeleteNotification}
                    handleMarkAsRead={handleMarkAsRead}
                    disableNotifications={disableNotifications}
                    notificationsEnabled={state.notificationsEnabled}
                    setUserNotifications={(updated) =>
                        setState({ userNotifications: updated })
                    }
                    user={state.user}
                    setToastMessage={setToastMessage}
                />
            </Grid>

            <UniversalModal
                onClose={() => setState({ openLegal: false })}
                open={state.openLegal}
                title="Legal Agreements"
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
            >
                {state.openLegal && (
                    <LegalUploader
                        onClose={() => setState({ openLegal: false })}
                    />
                )}
            </UniversalModal>
            <UniversalModal
                onClose={() => setState({ openUser: false })}
                open={state.openUser}
                title={` User ID: ${_id}`}
                timeStamp={` Username: ${username}`}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
            >
                <UserForm
                    row={user}
                    userAccount={true}
                    onClose={(user: any, newData?: any) =>
                        setModalVisbility(user, newData)
                    }
                    userFormPermissionCheck={false}
                />
            </UniversalModal>

            <UniversalModal
                onClose={() => setState({ openFaq: !openFaq })}
                open={openFaq}
                title="FAQ"
            >
                <FAQ />
            </UniversalModal>

            <UniversalModal
                onClose={() => setState({ openUserGuide: !openUserGuide })}
                open={openUserGuide}
                title="User Guide"
            >
                <UserGuidePdf />
            </UniversalModal>

            <ImpersonateDialog
                open={state.openEmulate}
                onClose={() => setState({ openEmulate: false })}
            />
            <ImpersonationBanner />
        </Grid>
    )
}

export default NavMenuAvatar
